<template>
  <div class="message-input-field">
    <div class="suggestions" v-if="showSuggestions">
      <ul>
        <li
          :class="{ selected: selectedIndex === index }"
          @click="addMention(suggestion)"
          v-for="(suggestion, index) in availableSuggestions"
          :key="suggestion.user_id"
        >
          {{ suggestion.user_name }}
          <!-- {{ selectedIndex === index ? "*" : "" }} -->
        </li>
      </ul>
    </div>
    <div
      :class="{ 'empty-input-field': textValue.length === 0 }"
      ref="input"
      contenteditable="true"
      @input="onInputEvent"
      @focus="onInputEvent"
      @keyup="onInputEvent"
      @click="onInputEvent"
    ></div>
  </div>
</template>

<script>
import * as sanitizeHtml from "sanitize-html";

const SANITIZE_HTML_CONFIG = {
  allowedTags: ["span"],
  allowedAttributes: {
    span: ["data-mention-*", { name: "class", values: ["mention"] }],
  },
};

export default {
  name: "MessageInputField",
  props: ["suggestions", "value"],
  data() {
    return {
      selectedIndex: 0,
      textNode: null,
      cursorPosition: -1,
      queryStartIndex: -1,
      query: null,
      mentions: [],
      textValue: "",
      htmlValue: "",
    };
  },
  computed: {
    showSuggestions() {
      return (
        this.query != null &&
        this.textNode != null &&
        this.cursorPosition !== -1 &&
        this.queryStartIndex !== -1 &&
        this.availableSuggestions.length > 0
      );
    },
    availableSuggestions() {
      return this.query?.length > 0
        ? this.suggestions.filter((it) =>
            it.user_name.toLowerCase().includes(this.query.toLowerCase())
          )
        : this.suggestions;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler({ textValue, htmlValue, mentions }) {
        this.textValue = textValue;
        this.mentions = mentions;
        this.htmlValue = htmlValue;

        if (
          this.$refs.input == null ||
          this.$refs.input.innerHTML === htmlValue
        )
          return;

        this.$refs.input.innerHTML = htmlValue;
      },
    },
    availableSuggestions(newVal) {
      if (this.selectedIndex >= newVal.length) {
        this.selectedIndex = 0;
      }
    },
  },
  mounted() {
    const observer = new MutationObserver(() => {
      if (
        this.$refs.input.querySelectorAll(`[data-mention-user-id]`).length === 0
      ) {
        this.mentions = [];
      }

      this.textValue = this.$refs.input.textContent;
      this.htmlValue = this.$refs.input.innerHTML;

      this.onChange();
    });

    observer.observe(this.$refs.input, {
      subtree: true,
      childList: true,
      characterData: true,
    });

    document.addEventListener("keydown", this.checkForTabKey);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.checkForTabKey);
  },
  methods: {
    onChange() {
      const payload = {
        textValue: this.textValue,
        htmlValue: this.htmlValue,
        mentions: this.mentions,
      };
      this.$emit("input", payload);
    },
    checkForTabKey(event) {
      if (event.code === "Tab" && this.showSuggestions) {
        event.preventDefault();
        this.addMention(this.availableSuggestions[this.selectedIndex]);
      } else if (event.code === "Enter" && event.target === this.$refs.input) {
        event.preventDefault();
        if (this.textValue && !this.showSuggestions) {
          const payload = {
            htmlValue: this.sanitize(this.htmlValue),
            textValue: this.textValue,
            mentions: this.mentions,
          };
          this.$emit("send", payload);
        }
      }
    },
    addMention(suggestion) {
      const parentNode = this.textNode.parentNode;
      const beforeNode = this.textNode.substringData(0, this.queryStartIndex);
      const afterNode = this.textNode.substringData(
        this.cursorPosition,
        this.textNode.length
      );
      const spaceNode = document.createTextNode("\xa0");

      const mentionContent = `@${suggestion.user_name}`;
      const mentionNode = document.createElement("span");
      mentionNode.setAttribute("data-mention-user-id", suggestion.user_id);
      mentionNode.setAttribute("data-mention-content", mentionContent);
      mentionNode.classList.add("mention");
      mentionNode.textContent = mentionContent;

      this.textNode.replaceWith(beforeNode, mentionNode, spaceNode, afterNode);
      this.$refs.input.normalize();

      const rangeOffset =
        Array.from(parentNode.childNodes).indexOf(mentionNode.nextSibling) + 1;

      const range = document.createRange();
      range.setStart(parentNode, rangeOffset);
      range.setEnd(parentNode, rangeOffset);
      range.collapse(true);

      const selection = document.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      this.textNode = null;
      this.cursorPosition = -1;
      this.queryStartIndex = -1;
      this.query = null;

      if (
        !this.mentions.some((mention) => mention.user_id === suggestion.user_id)
      ) {
        this.mentions = [...this.mentions, suggestion];
      }
    },
    removeMention(userId) {
      const mentionsCopy = [...this.mentions];
      const index = mentionsCopy.findIndex(
        (mention) => mention.user_id === userId
      );
      if (index === -1) return;
      mentionsCopy.splice(index, 1);
      this.mentions = mentionsCopy;
    },
    getCursorContext() {
      const selection = window.getSelection();

      if (!selection && !selection.length) {
        return null;
      }

      this.$refs.input.normalize();

      const range = selection.getRangeAt(0);

      const textNode = selection.anchorNode;
      const textContent = textNode.textContent;
      const textNodeParent = textNode.parentNode;
      const cursorPosition = range.startOffset;

      const textBeforeCursor = textContent.substring(0, cursorPosition);
      const textAfterCursor = textContent.substring(cursorPosition);

      this.$refs.input.normalize();

      return {
        textNode,
        textContent,
        textNodeParent,
        cursorPosition,
        textBeforeCursor,
        textAfterCursor,
      };
    },
    onInputEvent(event) {
      const context = this.getCursorContext();
      const parent = context.textNodeParent;

      if (event.code === "Tab") {
        event.preventDefault();
      } else if (
        this.showSuggestions &&
        event.type === "keyup" &&
        (event.code === "ArrowUp" || event.code === "ArrowDown")
      ) {
        event.preventDefault();
        if (event.code === "ArrowUp") {
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        } else {
          this.selectedIndex = Math.min(
            this.selectedIndex + 1,
            this.availableSuggestions.length - 1
          );
        }
      } else if (event.code === "Enter") {
        event.preventDefault();
        if (this.showSuggestions) {
          this.addMention(this.availableSuggestions[this.selectedIndex]);
        }
      } else if (parent.hasAttribute("data-mention-user-id")) {
        const originalText = context.textNodeParent.getAttribute(
          "data-mention-content"
        );
        if (context.textContent !== originalText) {
          event.preventDefault();

          if (
            context.textContent.match(new RegExp(`(.+)?(${originalText})(.+)?`))
          ) {
            const insertedData = context.textContent.replace(originalText, "");
            const indexOfInsertedData =
              context.textContent.indexOf(insertedData);
            context.textNode.replaceData(
              context.textContent.indexOf(insertedData),
              insertedData.length,
              ""
            );

            if (indexOfInsertedData === 0) {
              this.$refs.input.insertBefore(
                document.createTextNode(insertedData),
                context.textNode.parentNode
              );
            } else {
              this.$refs.input.insertBefore(
                document.createTextNode(insertedData),
                context.textNode.parentNode.nextSibling
              );
            }
          } else {
            const userId = parent.getAttribute("data-mention-user-id");

            let textNode = document.createTextNode(parent.textContent);
            parent.replaceWith(textNode);

            const range = document.createRange();
            range.setStart(textNode, context.cursorPosition);
            range.setEnd(textNode, context.cursorPosition);
            range.collapse(true);

            const selection = document.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);

            if (
              this.$refs.input.querySelectorAll(
                `[data-mention-user-id='${userId}']`
              ).length === 0
            ) {
              this.removeMention(userId);
            }
          }
        }
      } else {
        const { textBeforeCursor, cursorPosition, textNode } = context;
        const possibleMentionMatch = textBeforeCursor.match(/@(.+)?/) || [];
        const { 0: match, index } = possibleMentionMatch;

        if (match != null) {
          const query = match.substring(1);
          this.textNode = textNode;
          this.cursorPosition = cursorPosition;
          this.queryStartIndex = index;
          this.query = query;
        } else {
          this.query = null;
          this.textNode = null;
        }
      }
    },
    sanitize(html) {
      return sanitizeHtml(html, SANITIZE_HTML_CONFIG);
    },
  },
};
</script>

<style lang="scss" scoped>
.message-input-field {
  width: 100%;
  position: relative;
  max-width: 280px;
}

.suggestions {
  background: white;
  position: absolute;
  bottom: 150%;
  right: 85%;
  width: 422px;
  border-radius: 16px;
  background-color: #fff;
  padding: 32px 0;
  box-shadow: 0 0 32px rgba(0, 0, 0, 0.16);

  @media all and (max-width: 767px) {
    right: auto;
    left: -64px;
    width: calc(100vw - 32px);
    padding: 16px 0;
  }

  ul {
    list-style: none;
  }

  li {
    color: #000;
    font-family: "Nunito Sans", sans-serif;
    font-size: 16px;
    font-weight: 400;
    font-style: normal;
    letter-spacing: normal;
    line-height: 24px;
    text-align: left;
    display: flex;
    align-items: center;
    padding: 0 32px;
    background-color: transparent;
    transition: background-color 180ms;

    @media all and (max-width: 767px) {
      padding: 0 16px;
    }

    & + li {
      margin-top: 16px;
    }

    &.selected,
    &:hover {
      background-color: #f9f4ef;
      transition: background-color 180ms;
    }
  }
}

[contenteditable] {
  cursor: text;
  flex: 1 1 auto;
  color: #000;
  font-family: "Nunito Sans", sans-serif;
  font-size: 16px;
  font-weight: 700;
  font-style: normal;
  letter-spacing: normal;
  line-height: 24px;
  text-align: left;
  margin-left: 16px;
  margin-right: 16px;
}

.empty-input-field::before {
  content: "Type message";
  color: gray;
  font-weight: 400;
}
</style>
